import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Business, DeleteOutline, Person } from '@mui/icons-material';
import { useQueries } from '@tanstack/react-query';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { IPagination } from '../../Interfaces/IPagination';
import { ILegalTransactionRelationshipRealEstateOwner } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/ILegalTransactionRelationshipRealEstate';
import { customFetcher } from '../../hooks/useFetch';
import { useGetLegalClients } from '../../hooks/reactQuerys/LegalClientQueries';
import { ID_LEGAL_CLIENT_ASSET_ROLE_OWNER } from '../../hooks/reactQuerys/AssetQueries';

interface IProps {
  /** Eigentümer-Stand des Grundstücks (Teil des Vorgangs-Formulars). */
  owners: ILegalTransactionRelationshipRealEstateOwner[];
  /** Schreibt den neuen Stand in den Formular-State (gespeichert mit dem Vorgang). */
  setOwners: (owners: ILegalTransactionRelationshipRealEstateOwner[]) => void;
  /** Vorgangs-Mandanten zur schnellen Namensauflösung (optional). */
  knownClients?: ILegalClient[];
}

const buildClientName = (c?: ILegalClient | null): string | undefined => {
  if (!c) return undefined;
  const full = `${c.FirstName ?? ''} ${c.LastName ?? ''}`.trim();
  return full || `#${c.idLegalClient}`;
};

/** "1/2" | "0,5" | "" → number | null */
const parseShare = (raw: string): number | null => {
  const v = raw.trim();
  if (v === '') return null;
  if (v.includes('/')) {
    const [n, d] = v.split('/').map(s => Number(s.trim()));
    if (!isNaN(n) && !isNaN(d) && d !== 0) return n / d;
    return null;
  }
  const num = Number(v.replace(',', '.'));
  return isNaN(num) ? null : num;
};

const formatShare = (share?: number | null): string => {
  if (share == null) return '';
  const common: Record<string, string> = {
    '0.5': '1/2',
    '0.25': '1/4',
    '0.75': '3/4',
    '0.3333': '1/3',
    '0.6667': '2/3',
    '1': '1/1',
  };
  const key = share.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
  return common[key] ?? common[share.toFixed(4)] ?? String(share);
};

export const RealEstateOwners: React.FC<IProps> = ({
  owners,
  setOwners,
  knownClients,
}) => {
  // --- Namensauflösung -------------------------------------------------
  const knownMap = useMemo(
    () => new Map((knownClients ?? []).map(c => [c.idLegalClient, c])),
    [knownClients]
  );
  // Frisch gewählte Mandanten merken wir mit Namen, bis der Reload sie kennt.
  const [pickedMap, setPickedMap] = useState<Map<number, ILegalClient>>(
    new Map()
  );
  const missingIds = Array.from(
    new Set(
      owners
        .map(o => o.idLegalClient)
        .filter(id => !knownMap.has(id) && !pickedMap.has(id))
    )
  );
  const nameQueries = useQueries({
    queries: missingIds.map(id => ({
      queryKey: ['legalclient/fullObject', id] as const,
      queryFn: () =>
        customFetcher({
          endpoint: `/legalclient/fullObject/${id}`,
        }) as Promise<ILegalClient>,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    })),
  });
  const fetchedNameMap = useMemo(() => {
    const m = new Map<number, ILegalClient>();
    nameQueries.forEach(q => {
      if (q.data?.idLegalClient != null) m.set(q.data.idLegalClient, q.data);
    });
    return m;
  }, [nameQueries]);

  const clientOf = (id: number) =>
    knownMap.get(id) ?? pickedMap.get(id) ?? fetchedNameMap.get(id);
  const nameFor = (id: number) =>
    buildClientName(clientOf(id)) ?? `Mandant #${id}`;
  const isCompany = (id: number) => clientOf(id)?.idLegalClientType === 2;

  // --- Anteils-Eingabepuffer (damit Brüche beim Tippen erhalten bleiben) --
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const shareValue = (o: ILegalTransactionRelationshipRealEstateOwner) =>
    drafts[o.idLegalClient] ?? formatShare(o.share);

  // --- Mandanten-Suche -------------------------------------------------
  const [searchInput, setSearchInput] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput]);
  const searchUri =
    debounced.trim().length >= 2
      ? `search=${['LastName', 'FirstName', 'Email']
          .map(f => `${f}:${debounced.trim()}`)
          .join(';')};&searchtype=includes&page=1&rowsPerPage=20`
      : undefined;
  const { legalClients, isLoadingLegalClients } = useGetLegalClients(searchUri);
  const hits: ILegalClient[] =
    (legalClients as IPagination<ILegalClient[]> | undefined)?.results ?? [];

  const addOwner = (c: ILegalClient) => {
    if (owners.some(o => o.idLegalClient === c.idLegalClient)) return;
    setPickedMap(prev => new Map(prev).set(c.idLegalClient, c));
    setOwners([
      ...owners,
      {
        idLegalClient: c.idLegalClient,
        idLegalClientAssetRole: ID_LEGAL_CLIENT_ASSET_ROLE_OWNER,
        share: null,
      },
    ]);
    setSearchInput('');
  };

  const removeOwner = (id: number) => {
    setOwners(owners.filter(o => o.idLegalClient !== id));
    setDrafts(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const setShare = (id: number, raw: string) => {
    setDrafts(prev => ({ ...prev, [id]: raw }));
    setOwners(
      owners.map(o =>
        o.idLegalClient === id ? { ...o, share: parseShare(raw) } : o
      )
    );
  };

  const shareSum = owners.reduce((acc, o) => acc + (o.share ?? 0), 0);
  const shareSumWarn =
    owners.some(o => o.share != null) && Math.abs(shareSum - 1) > 0.001;

  return (
    <Box sx={{ my: 1 }}>
      <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant='subtitle2'>Eigentümer</Typography>
        <Chip size='small' label={owners.length} variant='outlined' />
      </Stack>

      {owners.length === 0 && (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
          Noch keine Eigentümer erfasst. Eigentümer werden beim Speichern dem
          Grundbuchblatt des Grundstücks zugeordnet (Übersicht „Grundstücke")
          und können z. B. aus der Grundbuch-/Eigentümerrecherche stammen.
        </Typography>
      )}

      <Stack spacing={0.75} sx={{ mb: 1 }}>
        {owners.map(o => (
          <Stack
            key={o.idLegalClient}
            direction='row'
            spacing={1}
            alignItems='center'
          >
            {isCompany(o.idLegalClient) ? (
              <Business fontSize='small' color='action' />
            ) : (
              <Person fontSize='small' color='action' />
            )}
            <Typography variant='body2' sx={{ flexGrow: 1 }}>
              {nameFor(o.idLegalClient)}
            </Typography>
            <TextField
              size='small'
              label='Anteil'
              placeholder='z. B. 1/2'
              value={shareValue(o)}
              onChange={e => setShare(o.idLegalClient, e.target.value)}
              sx={{ width: 110 }}
            />
            <Tooltip title='Eigentümer entfernen'>
              <IconButton
                size='small'
                onClick={() => removeOwner(o.idLegalClient)}
              >
                <DeleteOutline fontSize='small' />
              </IconButton>
            </Tooltip>
          </Stack>
        ))}
      </Stack>

      {shareSumWarn && (
        <Alert severity='warning' variant='outlined' sx={{ mb: 1, py: 0 }}>
          Die Summe der Anteile ergibt {formatShare(shareSum)} (≠ 1/1).
        </Alert>
      )}

      <Autocomplete<ILegalClient, false, false, false>
        size='small'
        filterOptions={x => x}
        options={hits}
        loading={isLoadingLegalClients}
        value={null}
        blurOnSelect
        clearOnBlur
        inputValue={searchInput}
        onInputChange={(_e, value, reason) => {
          if (reason === 'reset') return;
          setSearchInput(value);
        }}
        onChange={(_e, value) => {
          if (value) addOwner(value);
        }}
        getOptionLabel={c =>
          `${buildClientName(c)}${c.Email ? ` · ${c.Email}` : ''}`
        }
        isOptionEqualToValue={(a, b) => a.idLegalClient === b.idLegalClient}
        noOptionsText={
          searchInput.trim().length < 2
            ? 'Mindestens 2 Zeichen eingeben …'
            : 'Keine Treffer'
        }
        renderInput={params => (
          <TextField
            {...params}
            placeholder='Eigentümer suchen (Name, E-Mail) …'
            InputProps={{
              ...params.InputProps,
              startAdornment: <Add fontSize='small' sx={{ mr: 0.5 }} />,
              endAdornment: (
                <>
                  {isLoadingLegalClients ? (
                    <CircularProgress color='inherit' size={16} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};
